/**
 * Renders the "Output" section (section 2) of ProcessTaskSchemaEditorContent,
 * including output prefix, autopopulate toggle, output field table, task finder,
 * and the add/remove output schema button. Single-use component extracted from
 * ProcessTaskSchemaEditorContent to reduce its complexity.
 */
import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import ConfigureTaskFinderSection from './ConfigureTaskFinderSection';
import AutopopulateFromTasksField from './fields/AutopopulateFromTasksField';
import {ProcessSchemaFieldTable} from './ProcessSchemaFieldComponents';
import type {ResultSchemasGQL, StorableSchemaGQL} from './ProcessSchemaFieldComponents';
import ProcessSchemaMetadataSectionRow, {PrefixField} from './ProcessSchemaMetadataSectionRow';
import {EXECUTION_TYPES_WITH_TASK_FINDER} from './ProcessTaskSchemaEditorConstants';
import ProcessTaskSchemaEditorSection from './ProcessTaskSchemaEditorSection';
import {styles} from './ProcessTaskSchemaEditorStyles';
import type {ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import type {ProcessTaskSchemaEditorStateResult} from './useProcessTaskSchemaEditorState';

interface OutputConfigSectionProps {
  state: ProcessTaskSchemaEditorStateResult;
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  resultSchemas: Array<ResultSchemasGQL> | null;
  registryId?: string;
  isSettingsPage?: boolean;
}

function ToggleOutputSchemaButton(props: {hasOutputSchema: boolean; onClick: () => void}): JSX.Element {
  return (
    <div className={css(styles.outputSchemaToggle)}>
      <FormGroup className={classNames('u-no-spacing-bottom', css(styles.descriptionContents))}>
        <div className="settingsPageContainer-label">
          <Button
            buttonStyle={ButtonStyle.LINK}
            onClick={props.onClick}
            className={props.hasOutputSchema ? css(styles.removeText) : ''}
            dataTestAttrs={{
              component: 'ProcessTaskSchemaEditor',
              element: 'outputSchemaControlButton',
              key: props.hasOutputSchema ? 'remove' : 'add',
            }}
          >
            {props.hasOutputSchema ? 'Remove output schema' : 'Add output schema'}
          </Button>
        </div>
        <div className={css(styles.descriptionText)}>
          {`Output schema cannot be ${props.hasOutputSchema ? 'removed' : 'added'} after the task schema is
          created.`}
        </div>
      </FormGroup>
    </div>
  );
}

export default function OutputConfigSection(props: OutputConfigSectionProps): JSX.Element {
  const {state, processTaskSchema} = props;

  return (
    <ProcessTaskSchemaEditorSection
      isLastSection={!state.canConfigureFlowchart}
      sectionTitle="Output"
      sectionNumber={2}
      actionButton={
        !state.isBeingCreated && processTaskSchema?.outputSchema?.id
          ? {
              onClick: () => {
                copyToClipboard(processTaskSchema.outputSchema!.id, {
                  showToastOnSuccess: true,
                  toastText: 'Copied output schema API ID to clipboard',
                });
              },
              tooltipTitle: 'Copy API ID',
              icon: 'note-code',
            }
          : undefined
      }
      shouldHideSectionHeader={state.shouldHideSectionHeaders}
    >
      {state.hasOutputSchema && (
        <>
          <ProcessSchemaMetadataSectionRow>
            <PrefixField
              label="ID prefix"
              value={state.outputSchemaPrefix}
              error={state.errors.outputSchemaPrefix ?? ''}
              isBeingCreated={state.isBeingCreated}
              isDisabled={state.isArchived || state.isEditingFlowchart || state.isLocked}
              onChange={async (updatedOutputSchemaPrefix: string) => {
                if (updatedOutputSchemaPrefix === state.outputSchemaPrefix) {
                  return;
                }
                if (!state.isBeingCreated) {
                  await state.updateProcessTaskSchemaWithFields({
                    outputSchemaPrefix: updatedOutputSchemaPrefix,
                  });
                } else {
                  state.setOutputSchemaPrefix(updatedOutputSchemaPrefix);
                }
              }}
              prefixPreview={state.generatePrefixPreview('Output ID preview', state.outputSchemaPrefix)}
              dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputPrefixField'}}
            />
          </ProcessSchemaMetadataSectionRow>
          {state.executionType === ProcessExecutionType.DIRECT && (
            <ProcessSchemaMetadataSectionRow>
              <AutopopulateFromTasksField
                value={state.autopopulateDirectOutputsFromTasks}
                disabled={state.isLocked}
                onChange={async (value) => {
                  if (value === state.autopopulateDirectOutputsFromTasks) {
                    return;
                  }
                  if (!state.isBeingCreated) {
                    await state.updateProcessTaskSchemaWithFields({
                      autopopulateDirectOutputsFromTasks: value,
                    });
                  } else {
                    state.setAutopopulateDirectOutputsFromTasks(value);
                  }
                }}
              />
            </ProcessSchemaMetadataSectionRow>
          )}
          <ErrorList errorMessage={state.errors.outputFields} />
          <ProcessSchemaFieldTable
            isDisabled={state.isArchived || state.isEditingFlowchart || state.isLocked}
            key={state.outputFieldTableKey}
            hasBottomLink={state.hasOutputSchema}
            schema={processTaskSchema?.outputSchema ?? null}
            ownerId={state.schemaOwner!.id}
            ownerUrlId={state.schemaOwner!.url_identifier}
            registryId={props.registryId}
            onFieldsChange={(updatedOutputFields) => {
              state.onFieldsChanged(
                state.outputFields,
                updatedOutputFields,
                'outputFields',
                state.setOutputFields
              );
            }}
            schemaType="output"
            storableSchemas={props.storableSchemas}
            fields={state.outputFields}
            resultSchemas={props.resultSchemas}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputTable'}}
            compactMode={props.isSettingsPage === false}
          />
          {EXECUTION_TYPES_WITH_TASK_FINDER.has(state.executionType) && (
            <ConfigureTaskFinderSection
              isBeingCreated={state.isBeingCreated}
              isLoading={!state.haveSchemasLoaded}
              taskFinderLookupConfig={state.taskFinderLookupConfig}
              outputSchema={processTaskSchema?.outputSchema ?? null}
              taskSchema={processTaskSchema ?? null}
              isArchived={state.isArchived}
              error={state.errors.taskFinderLookupConfig}
              onChange={(updatedLookupConfig) => {
                state.updateProcessTaskSchemaWithFields({
                  taskFinderLookupConfig: updatedLookupConfig,
                  updateTaskFinder: true,
                });
              }}
              locked={state.isLocked}
            />
          )}
        </>
      )}
      {state.isBeingCreated && (
        <ToggleOutputSchemaButton
          hasOutputSchema={state.hasOutputSchema}
          onClick={state.toggleHasOutputSchemaButton}
        />
      )}
      {!state.hasOutputSchema && !state.isBeingCreated && (
        <div className={css(styles.descriptionText)}>
          Output schema cannot be added after the task schema is created.
        </div>
      )}
    </ProcessTaskSchemaEditorSection>
  );
}
